<template>
	<div style="position: relative; padding-top: 35px">
		<span
			ref="tttext"
			style="position: absolute; top: 0"
			v-if="!hasError"
			class="hist-text"
			:class="{ hidden: !tipVisible }"
			>{{ tooltipText }}</span
		>
		<span
			style="
				text-align: center !important;
				display: block;
				margin-bottom: 25px;
				white-space: wrap !important;
				font-size: 10pt;
				color: #c77171;
			"
			v-if="hasError"
			><i class="fa fa-exclamation-triangle"></i><br />
			An error occured trying to retrieve the histogram. The dataset column may be
			corrupted.</span
		>
		<canvas
			v-show="!hasError"
			:height="height"
			:width="width"
			:class="className"
			style="border-bottom: solid 1px #333"
			v-on:mousemove="onMouseMove"
			v-on:mouseleave="tipVisible = false"
			v-on:mouseenter="onMouseEnter($event), (tipVisible = true)"
		/>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import vtkPiecewiseGaussianWidget from "vtk.js/Sources/Interaction/Widgets/PiecewiseGaussianWidget";

// ----------------------------------------------------------------------------
// Helper method
// ----------------------------------------------------------------------------

const normalize = (bins, yOffsetPercent) => {
	let maxCount = 0;
	for (let i = 0; i < bins.length; i++) {
		if (bins[i] > maxCount) {
			maxCount = bins[i];
		}
	}
	const scale = 1 - yOffsetPercent;
	return bins.map((v) => (scale * v) / maxCount + yOffsetPercent);
};

// ----------------------------------------------------------------------------

const logBins = (bins, dataRange) => {
	const targetSize = Math.floor(bins.length / 5);
	const linearDelta = (dataRange[1] - dataRange[0]) / (bins.length - 1);
	let logMin = Math.log10(dataRange[0]);
	if (logMin == -Infinity) {
		logMin = 0;
	}
	const logMax = Math.log10(dataRange[1]);
	const logDelta = logMax - logMin;
	const results = [];
	const ranges = [];
	while (results.length < targetSize) {
		results.push(0);
	}
	//console.log("dataRange",logMax)
	// Fill results
	for (let logBinIdx = 1; logBinIdx <= targetSize; logBinIdx++) {
		const minBinRange = Math.pow(10, logMin + ((logBinIdx - 1) / targetSize) * logDelta);
		const maxBinRange = Math.pow(10, logMin + (logBinIdx / targetSize) * logDelta);
		ranges.push([minBinRange, maxBinRange]);
		for (let linearBinIdx = 0; linearBinIdx < bins.length; linearBinIdx++) {
			const value = dataRange[0] + linearBinIdx * linearDelta;
			if (value >= minBinRange && value < maxBinRange) {
				results[logBinIdx - 1] += bins[linearBinIdx];
			}
		}
	}

	return [results, ranges];
};

// ----------------------------------------------------------------------------

const linearBins = (bins, dataRange) => {
	const ranges = [];
	const delta = (dataRange[1] - dataRange[0]) / (bins.length - 1);
	for (let i = 0; i < bins.length; i++) {
		const base = dataRange[0] + i * delta;
		ranges.push([base, base + delta]);
	}
	return [bins, ranges];
};

// ----------------------------------------------------------------------------
// Component API
// ----------------------------------------------------------------------------

export default defineComponent({
	name: "imdex-histogram",
	props: {
		gradient: {
			type: Array,
			default: () => []
		},
		dataRange: {
			type: Array,
			default: () => [0, 1]
		},
		width: {
			type: Number,
			default: 256
		},
		height: {
			type: Number,
			default: 100
		},
		className: {
			type: String,
			default: ""
		},
		bins: {
			type: Array,
			default: () => [0, 0]
		},
		normalize: {
			type: Boolean,
			default: true
		},
		yOffset: {
			type: Number,
			default: 0.2
		},
		image: {
			//type: HTMLImageElement,
			default: null
		},
		// what portion of the data range should the background (colormap) image be drawn in.
		imageRange: {
			type: Array,
			default: () => [0, 1]
		},
		// color to fill between dataRange[0] and imageRangep[0]
		lowFill: {
			type: String,
			default: ""
		},
		// color to fill between dataRange[1] and imageRangep[1]
		highFill: {
			type: String,
			default: ""
		},
		logScale: {
			type: Boolean,
			default: false
		},
		inverted: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			tooltipText: "",
			xOffset: 0,
			binRangesToUse: null,
			binValuesToUse: null,
			tipVisible: false,
			hasError: false
		};
	},
	mounted() {
		this.drawChart();
	},
	methods: {
		onMouseEnter(e) {
			const { left } = e.target.getBoundingClientRect();
			this.xOffset = left;

			this.onMouseMove(e);
		},
		onMouseMove(e) {
			if (!this.binValuesToUse || !this.binRangesToUse) {
				this.tooltipText = "";
				return;
			}

			const ratio = Math.min(1, Math.max(0, (e.clientX - this.xOffset) / this.width));
			const index = Math.round((this.binValuesToUse.length - 1) * ratio);
			const valueList = `${this.binValuesToUse[index]}`.split("");
			const ranges = this.binRangesToUse[index];
			const resultValueList = [];

			while (valueList.length) {
				if (!(valueList.length % 3) && resultValueList.length) {
					resultValueList.push("'");
				}
				resultValueList.push(valueList.shift());
			}

			this.tooltipText = `[${ranges[0].toFixed(3)}, ${ranges[1].toFixed(3)}] - ${this.$t(
				`ssa.displayPane.count`
			)} ${resultValueList.join("")}`;
		},
		drawChart() {
			if (!this.$el || !this.bins) {
				return;
			}
			const ctx = this.$el.querySelector("canvas").getContext("2d");
			if (ctx) {
				const [rawBins, ranges] = this.logScale
					? logBins(this.bins, this.dataRange)
					: linearBins(this.bins, this.dataRange);
				const NanCheck = ranges.filter((item) => {
					for (const i in item) {
						if (isNaN(item[i])) {
							return true;
						}
						return false;
					}
				});
				this.hasError = NanCheck.length > 0;

				this.binRangesToUse = ranges;
				this.binValuesToUse = rawBins;

				const bins = this.normalize ? normalize(rawBins, this.yOffset) : rawBins;
				ctx.clearRect(0, 0, this.width, this.height);
				ctx.save();
				if (!this.image || !this.image.complete) {
					vtkPiecewiseGaussianWidget.drawChart(
						ctx,
						[0, 0, this.width, this.height],
						bins,
						{
							lineWidth: 1,
							strokeStyle: "#333",
							fillStyle: "rgba(255,255,255,0.5)"
						}
					);
				} else {
					vtkPiecewiseGaussianWidget.drawChart(
						ctx,
						[0, 0, this.width, this.height],
						bins,
						{
							lineWidth: 1,
							strokeStyle: "#333",
							fillStyle: "rgba(255,255,255,1)",
							clip: true
						}
					);
					// shrink the background colormap image to fit the user data range.
					let startX = 0;
					let endX = this.width;
					const scaleX = this.width / (this.dataRange[1] - this.dataRange[0]);
					if (this.dataRange[0] !== this.imageRange[0]) {
						startX = (this.imageRange[0] - this.dataRange[0]) * scaleX;
						ctx.fillStyle = this.gradient[0].color;
						ctx.fillRect(0, 0, startX + 0.5, this.height);
					}
					if (this.dataRange[1] !== this.imageRange[1]) {
						endX = (this.imageRange[1] - this.dataRange[0]) * scaleX;
						ctx.fillStyle = this.gradient[this.gradient.length - 1].color;
						ctx.fillRect(endX - 0.5, 0, this.width - endX + 1, this.height);
					}
					const gradient = ctx.createLinearGradient(startX, 0, endX, 0);
					for (let i = 0; i < this.gradient.length; i++) {
						const d = this.gradient;
						//const d = this.inverted ? JSON.parse(JSON.stringify(this.gradient)).reverse() : JSON.parse(JSON.stringify(this.gradient));
						gradient.addColorStop(d[i].stop / 100, d[i].color);
					}
					ctx.fillStyle = gradient;
					ctx.fillRect(startX, 0, endX - startX, this.height);
				}
				ctx.restore();
			}
		}
	},

	watch: {
		image(val) {
			if (val != "") {
				this.drawChart();
				if (this.image && !this.image.complete) {
					//this.image.onload = () => this.drawChart();
				}
			}
		},
		bins() {
			this.drawChart();
		},
		width() {
			this.$nextTick(() => {
				this.drawChart();
			});
		}
	}
});
</script>
<style lang="scss">
.hist-text {
	font-size: 9pt !important;
	text-transform: uppercase;
	letter-spacing: 0px;
	font-family: monospace;
	font-weight: bold;
}
</style>
